import "@styles/common.css";
import "@styles/font.css";
import "@styles/typography.css";
import "@styles/base.css";
import "@styles/colors.css";
import "@styles/variables.css";
import "@styles/transitions.css";
import "@styles/locomotive-scroll.css";

import Header from "@blocks/Header";
import Footer from "@blocks/Footer";
import Loader from "@blocks/Loader";
import Works from "@blocks/Works";
import Paperbar from "@blocks/Paperbar";
import Intro from "@blocks/Intro";
import IntroText from "@blocks/IntroText";
import Contact from "@blocks/Contact";
import Nav from "@blocks/Nav";
import Dots from "@blocks/Dots";
import PageTransitioner from "@blocks/PageTransitioner";
import Events from "@blocks/Events";
import AboutIntro from "@blocks/AboutIntro";
import ScrollDown from "@blocks/ScrollDown";
import Splash from "@blocks/Splash";

import Columns from "@components/Columns";
import Cursor from "@components/Cursor";
import Colors from "@components/Colors";
import AnimatedText from "@components/AnimatedText";
import Anchor from "@components/Anchor";

import About from "@pages/About";
import PageEvents from "@pages/Events";

import Raf from "@utils/Raf";
import Observer from "@utils/Observer";
import Scroll from "@utils/Scroll";

import Router from "@router/index.js";

const MODULES = [
  Header,
  Footer,
  Nav,
  Dots,
  Works,
  Paperbar,
  Intro,
  IntroText,
  PageTransitioner,
  Events,
  Columns,
  Cursor,
  Contact,
  AnimatedText,
  Anchor,
  About,
  PageEvents,
  AboutIntro,
  ScrollDown,
  Splash,
];

class App {
  lastIndex = 0;

  constructor() {
    window.addEventListener("DOMContentLoaded", this.initApp);
    window.addEventListener("resize", this.onResize);
    window.addEventListener("scroll", this.onScroll);
  }

  initApp = async () => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").then(
        function (registration) {
          // Registration was successful
          console.log(
            "ServiceWorker registration successful with scope: ",
            registration.scope
          );
        },
        function (err) {
          // registration failed :(
          console.log("ServiceWorker registration failed: ", err);
        }
      );
    }

    this.router = new Router();
    this.loader = new Loader(document.querySelector(".loader"));
    this.observer = new Observer();
    this.raf = new Raf();
    this.scroll = new Scroll();
    this.lastIndex = 0;
    this.instances = [];

    await this.loader.loadFontFaces();

    const resourcesLoaded = this.loader.load();
    const itemsInited = this.initItems();
    await Promise.all([resourcesLoaded, itemsInited]);

    //this.router = new Router();

    await this.initClones();

    this.initColors();

    this.scroll.update();

    await this.loader.hide();

    // Call onLoaderHidden method if any
    await Promise.all(
      this.instances.map(async (instance) => {
        if (instance.onLoaderHidden) {
          await instance.onLoaderHidden();
        }
      })
    );

    this.scroll.onComplete();
  };

  initColors = () => {
    this.colors = new Colors();
  };

  initItems = () => {
    return new Promise(async (resolve, reject) => {
      // Create module instances
      const newInstances = MODULES.filter((module) =>
        module.hasOwnProperty("selector")
      ).flatMap((Module) => {
        const blocks = [...document.querySelectorAll(Module.selector)];
        return blocks
          .filter((block) => !block.dataset["instanceIndex" + Module.name])
          .map((block) => {
            const previousIndexes = block.dataset.instanceIndex;
            block.dataset.instanceIndex = previousIndexes
              ? [previousIndexes, this.lastIndex].join(",")
              : this.lastIndex;
            block.dataset["instanceIndex" + Module.name] = this.lastIndex;

            this.lastIndex++;

            return new Module(block);
          });
      });

      // Call onReady method if any
      await Promise.all(
        newInstances.map(async (instance) => {
          if (instance.onReady) {
            await instance.onReady();
          }
        })
      );

      this.instances = this.instances.concat(newInstances);

      document.body.classList.add("loaded");

      resolve(newInstances);
    });
  };

  initClones = async () => {
    return new Promise(async (resolve, reject) => {
      this.instances = this.instances.concat(
        MODULES.flatMap((module) => {
          if (!module.hasOwnProperty("selector")) {
            return;
          }

          const blocks = [...document.querySelectorAll(module.selector)];
          return blocks
            .map((block) => {
              const blockInstanceIndex = block.getAttribute(
                "data-instance-index"
              );
              if (blockInstanceIndex && blockInstanceIndex !== "") {
                return null;
              }

              const previousIndexes = block.dataset.instanceIndex;
              block.dataset.instanceIndex = previousIndexes
                ? [previousIndexes, this.lastIndex].join(",")
                : this.lastIndex;
              block.dataset["instanceIndex" + module.name] = this.lastIndex;

              this.lastIndex++;

              return new module(block);
            })
            .filter((v) => !!v);
        })
      );

      await Promise.all(
        this.instances.map(async (instance) => {
          if (instance.onReady && !instance.mounted) {
            await instance.onReady();
          }
        })
      );

      await Promise.all(
        this.instances.map(async (instance) => {
          if (instance.onComplete && !instance.completed) {
            await instance.onComplete();
          }
        })
      );

      resolve();
    });
  };

  onResize = (e) => {
    if (!this.instances) {
      return;
    }

    //document.documentElement.style.setProperty("--vh", `${vh()}px`);

    this.instances
      .filter((i) => !!i)
      .forEach((instance) => {
        if (instance.onResize) {
          instance.onResize(e);
        }
      });
  };

  getOrInitScroll = () => {
    if (document.querySelector("[data-scroll-container]") && !this.scroll) {
      this.scroll = new Scroll();
    }

    return this.scroll;
  };

  onPageChangeComplete = () => {
    return new Promise(async (resolve, reject) => {
      await Promise.all(
        this.instances.map(async (instance) => {
          if (instance && instance.onPageChangeComplete) {
            await instance.onPageChangeComplete();
          }
        })
      );

      resolve();
    });
  };
}

window.App = new App();

export const getRaf = () => {
  return window.App.raf;
};

export const getObserver = () => {
  return window.App.observer;
};

export const getInstance = (el) => {
  const instanceIndex = el.getAttribute("data-instance-index");

  if (!instanceIndex) {
    return null;
  }

  return window.App.instances[parseInt(instanceIndex, 10)];
};

export const getScroll = () => {
  return window.App.getOrInitScroll();
};
